import { config } from "../../../config";
import { isFixedDateEvent, isLastMomentSendEvent } from "../../../utils/events";
import { type ISO8601_TIME } from "../../../utils/iso8601";
import type { SerialisedLexicalEditorDesign } from "../../message/editor/types/serialised-editor-design";
import type { FollowUpMomentDetailsConfig } from "../hooks/use-follow-up-moment-details";
import {
  type FollowUpChannelWithData,
  type FollowUpModeSchemaType,
  type FollowUpSchemaType,
} from "../reducer/schema";
import type { FollowUpDetailsWithConfig, FollowUpMomentDetails } from "./types";

export function momentToFollowUp(
  moment: FollowUpMomentDetails,
  followUp?: FollowUpDetailsWithConfig,
  followUpMomentDetailsConfig?: FollowUpMomentDetailsConfig,
): FollowUpSchemaType {
  const base = {
    title: followUp?.title ?? `Follow Up: ${moment.title}`,
    design: (followUp?.design ?? null) as SerialisedLexicalEditorDesign | null,
    reason: followUp?.config ?? defaultReason(moment, followUpMomentDetailsConfig),
  };
  return {
    ...base,
    ...channelHandler(moment, followUp),
    ...convertMomentScheduleToFollowUpSchedule(moment, followUp),
  };
}

function defaultReason(
  moment: FollowUpMomentDetails,
  followUpMomentDetailsConfig?: FollowUpMomentDetailsConfig,
): FollowUpSchemaType["reason"] {
  if (followUpMomentDetailsConfig?.type === "from_survey") {
    const survey = moment.surveys?.find((s) => s.id === followUpMomentDetailsConfig.data.survey_id);

    if (survey) {
      return { type: "no_survey_completion", source_id: moment.id, survey_id: survey.id };
    }
  }

  return { type: "no_interaction", source_id: moment.id };
}

function channelHandler(
  moment: FollowUpMomentDetails,
  followUp?: FollowUpDetailsWithConfig,
): FollowUpChannelWithData {
  const channel = followUp?.channel ?? moment.channel;

  switch (channel) {
    case "slack":
      return {
        channel: "slack",
        channelData: { slack_alias_id: followUp?.from ?? moment.from },
      };
    case "teams":
      return {
        channel: "teams",
        channelData: undefined,
      };
    case "twilio":
      return {
        channel: "twilio",
        channelData: undefined,
      };
    case "work_email":
    case "personal_email":
      return {
        channel,
        channelData: {
          email_sender: followUp?.from ?? moment.from ?? config.defaultEmailFrom,
          email_subject: followUp?.email_subject ?? moment.email_subject ?? "",
          email_design_id: followUp?.email_design_id ?? moment.email_design_id ?? null,
        },
      };
    case null:
      return {
        channel: "work_email",
        channelData: {
          email_sender: followUp?.from ?? moment.from ?? config.defaultEmailFrom,
          email_subject: followUp?.email_subject ?? moment.email_subject ?? "",
          email_design_id: followUp?.email_design_id ?? moment.email_design_id ?? null,
        },
      };
  }
}

export function convertMomentScheduleToFollowUpSchedule(
  moment: FollowUpMomentDetails,
  followUp?: Pick<
    FollowUpDetailsWithConfig,
    "immediate" | "schedule" | "timezone" | "schedule_event"
  >,
): FollowUpModeSchemaType {
  if (!followUp) return defaultSchedule(moment.status === "finished");

  const { schedule, timezone, immediate } = followUp;

  if (immediate) return { mode: "finished", schedule: { type: "immediate" } };

  if (!schedule) return defaultSchedule(moment.status === "finished");

  if (isFixedDateEvent(schedule.event_key)) {
    return {
      mode: "finished",
      schedule: {
        type: "specific",
        timezone,
        date: followUp.schedule_event?.date ?? null,
        time: schedule.send_at as ISO8601_TIME,
      },
    };
  }

  if (isLastMomentSendEvent(schedule.event_key)) {
    // We only allow a single interval for follow ups
    const intervalData = schedule.interval_json ? Object.entries(schedule.interval_json) : [];
    if (intervalData.length !== 1) {
      return defaultSchedule(moment.status === "finished");
    }
    const [intervalType, intervalValue] = intervalData[0] as [
      "days" | "weeks" | "months" | "years",
      number,
    ];

    return {
      mode: "scheduled",
      schedule: {
        type: "scheduled",
        timezone,
        amount: intervalValue,
        period: intervalType,
        time: schedule.send_at as ISO8601_TIME,
        allow_weekends: schedule.on_event_key?.includes("saturday") ?? false,
      },
    };
  }

  return defaultSchedule(moment.status === "finished");
}

function defaultSchedule(momentFinished: boolean): FollowUpModeSchemaType {
  if (momentFinished) {
    return { mode: "finished", schedule: { type: "immediate" } };
  } else {
    return {
      mode: "scheduled",
      schedule: {
        type: "scheduled",
        amount: null,
        period: null,
        time: null,
        timezone: null,
        allow_weekends: false,
      },
    };
  }
}
